import React, { useState } from 'react';
import Toast from '@/components/common/Toast';
import { CertStatus, EntFormData } from './types';
import { ImageUploadBox, Select, SendCodeButton } from '@/components/common';
import {
  fetchSendVerificationChallenge,
  fetchVerifyVerificationChallenge,
} from '@/service/api/verification';

interface EnterpriseCertFormProps {
  enterpriseStatus: CertStatus;
  rejectReason?: string;
  formData: EntFormData;
  submitting: boolean;
  onChange: (data: Partial<EntFormData>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

// 校验正则表达式
const PHONE_REGEX = /^1[3-9]\d{9}$/;
const ID_CARD_REGEX = /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/;
const CREDIT_CODE_REGEX = /^[0-9A-HJ-NP-RT-UW-XY]{18}$/i;

// 法定代表人证件类型列表
const LEGAL_ID_TYPE_OPTIONS = [
  { value: 'ID_CARD', label: '居民身份证' },
  { value: 'HK_RESIDENCE_PERMIT', label: '港澳居民居住证' },
  { value: 'MACAU_RESIDENCE_PERMIT', label: '澳门居民居住证' },
  { value: 'TAIWAN_RESIDENCE_PERMIT', label: '台湾居民居住证' },
  { value: 'HK_MACAU_TAIWAN_RESIDENCE_PERMIT', label: '港澳台居民居住证' },
  { value: 'FOREIGN_PERMANENT_RESIDENCE', label: '外国人永久居留身份证' },
  { value: 'HK_MACAU_HOME_RETURN_PERMIT', label: '港澳居民来往内地通行证(回乡证)' },
  { value: 'TAIWAN_COMPATRIOT_PERMIT', label: '台湾居民来往大陆通行证(台胞证)' },
];

export const EnterpriseCertForm: React.FC<EnterpriseCertFormProps> = ({
  enterpriseStatus,
  rejectReason,
  formData,
  submitting,
  onChange,
  onSubmit,
  onCancel,
}) => {
  const [errors, setErrors] = useState<{
    companyName?: string;
    creditCode?: string;
    legalPerson?: string;
    legalId?: string;
    contactName?: string;
    contactPhone?: string;
    verificationCode?: string;
  }>({});

  const [verifyingCode, setVerifyingCode] = useState<boolean>(false);
  const [uploadBoxKey, setUploadBoxKey] = useState<number>(0);
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' | 'info' | 'warning' } | null>(null);

  const currentIdType = formData.legalRepresentativeIdType || 'ID_CARD';

  const validateField = (
    field: 'companyName' | 'creditCode' | 'legalPerson' | 'legalId' | 'contactName' | 'contactPhone' | 'verificationCode',
    value: string
  ) => {
    let errorMsg = '';
    if (field === 'companyName') {
      if (!value || value.trim().length === 0) {
        errorMsg = '请输入企业全称';
      }
    } else if (field === 'creditCode') {
      if (value && !CREDIT_CODE_REGEX.test(value)) {
        errorMsg = '请输入正确的 18 位统一社会信用代码';
      }
    } else if (field === 'legalPerson') {
      if (!value || value.trim().length === 0) {
        errorMsg = '请输入法定代表人姓名';
      }
    } else if (field === 'legalId') {
      if (value) {
        if (currentIdType === 'ID_CARD' && !ID_CARD_REGEX.test(value)) {
          errorMsg = '请输入正确的 18 位身份证号码';
        } else if (value.trim().length === 0) {
          errorMsg = '请输入证件号码';
        }
      }
    } else if (field === 'contactName') {
      if (!value || value.trim().length === 0) {
        errorMsg = '请输入经办人姓名';
      }
    } else if (field === 'contactPhone') {
      if (value && !PHONE_REGEX.test(value)) {
        errorMsg = '请输入正确的 11 位手机号码';
      }
    } else if (field === 'verificationCode') {
      if (value && value.trim().length < 4) {
        errorMsg = '请输入有效的验证码';
      }
    }
    setErrors((prev) => ({ ...prev, [field]: errorMsg }));
  };

  const validateAll = () => {
    const newErrors: {
      companyName?: string;
      creditCode?: string;
      legalPerson?: string;
      legalId?: string;
      contactName?: string;
      contactPhone?: string;
      verificationCode?: string;
    } = {};

    if (!formData.companyName || !formData.companyName.trim()) {
      newErrors.companyName = '请输入企业全称';
    }

    if (!formData.creditCode || !CREDIT_CODE_REGEX.test(formData.creditCode)) {
      newErrors.creditCode = '请输入正确的 18 位统一社会信用代码';
    }

    if (!formData.legalPerson || !formData.legalPerson.trim()) {
      newErrors.legalPerson = '请输入法定代表人姓名';
    }

    if (!formData.legalId) {
      newErrors.legalId = '请输入法定代表人证件号码';
    } else if (currentIdType === 'ID_CARD' && !ID_CARD_REGEX.test(formData.legalId)) {
      newErrors.legalId = '请输入正确的 18 位身份证号码';
    }

    if (!formData.contactName || !formData.contactName.trim()) {
      newErrors.contactName = '请输入运营经办人姓名';
    }

    if (!formData.contactPhone || !PHONE_REGEX.test(formData.contactPhone)) {
      newErrors.contactPhone = '请输入正确的 11 位手机号码';
    }

    if (!formData.operatorPhoneVerificationCode) {
      newErrors.verificationCode = '请输入手机验证码';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendCode = async (): Promise<boolean> => {
    if (!formData.contactPhone || !PHONE_REGEX.test(formData.contactPhone)) {
      setErrors((prev) => ({ ...prev, contactPhone: '请输入正确的 11 位手机号码' }));
      return false;
    }
    try {
      await fetchSendVerificationChallenge({
        purpose: 'ENTERPRISE_ONBOARDING_OPERATOR_PHONE',
        target: formData.contactPhone,
      });
      setToast({ message: '验证码已发送，请查收', type: 'success' });
      return true;
    } catch (err: any) {
      const errMsg = err?.message || err?.msg || '发送验证码失败';
      setToast({ message: errMsg, type: 'error' });
      setErrors((prev) => ({ ...prev, verificationCode: errMsg }));
      return false;
    }
  };

  const handleVerifyCode = async (): Promise<boolean> => {
    const code = formData.operatorPhoneVerificationCode?.trim();
    if (!formData.contactPhone || !PHONE_REGEX.test(formData.contactPhone)) {
      setErrors((prev) => ({ ...prev, contactPhone: '请输入正确的 11 位手机号码' }));
      return false;
    }
    if (!code || code.length < 4) {
      setErrors((prev) => ({ ...prev, verificationCode: '请输入有效的 6 位验证码' }));
      return false;
    }
    setVerifyingCode(true);
    try {
      const res = await fetchVerifyVerificationChallenge({
        purpose: 'ENTERPRISE_ONBOARDING_OPERATOR_PHONE',
        target: formData.contactPhone,
        code,
      });
      if (res?.verification_token) {
        onChange({ operatorPhoneVerificationToken: res.verification_token });
        setToast({ message: '手机验证码校验成功！', type: 'success' });
        setErrors((prev) => ({ ...prev, verificationCode: undefined }));
        return true;
      } else {
        const msg = '获取验证 Token 失败，请重试';
        setToast({ message: msg, type: 'error' });
        setErrors((prev) => ({ ...prev, verificationCode: msg }));
        return false;
      }
    } catch (err: any) {
      const msg = err?.message || err?.msg || '验证码校验失败，请检查验证码是否正确';
      setToast({ message: msg, type: 'error' });
      setErrors((prev) => ({ ...prev, verificationCode: msg }));
      return false;
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateAll()) {
      return;
    }
    if (!formData.operatorPhoneVerificationToken && formData.operatorPhoneVerificationCode) {
      const ok = await handleVerifyCode();
      if (!ok) {
        return;
      }
    }
    onSubmit(e);
  };

  return (
    <div className="border-t border-background-200/70 pt-6">
      {enterpriseStatus === 'rejected' && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-100/60">
          <p className="text-xs text-red-600">
            <strong>驳回原因：</strong>{rejectReason || '您的企业认证未通过审核，请仔细核对后重新提交。'}
          </p>
        </div>
      )}
      <h3 className="font-semibold text-sm text-foreground-950 mb-4">填写企业认证信息</h3>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* 企业全称 */}
        <div>
          <label className="block text-xs font-medium text-foreground-700 mb-1.5">
            <span className="text-red-500 mr-1">*</span>企业全称
          </label>
          <input
            type="text"
            value={formData.companyName}
            onChange={(e) => {
              const val = e.target.value;
              onChange({ companyName: val });
              if (errors.companyName) validateField('companyName', val);
            }}
            onBlur={(e) => validateField('companyName', e.target.value)}
            placeholder="请输入营业执照上的企业全称"
            className={`w-full px-4 py-3 rounded-lg border bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:ring-2 transition-all ${errors.companyName
              ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
              : 'border-gray-200 focus:border-primary-400/60 focus:ring-primary-100/50'
              }`}
            required
          />
          {errors.companyName && (
            <p className="text-xs text-red-500 mt-1">{errors.companyName}</p>
          )}
        </div>

        {/* 统一社会信用代码 */}
        <div>
          <label className="block text-xs font-medium text-foreground-700 mb-1.5">
            <span className="text-red-500 mr-1">*</span>统一社会信用代码
          </label>
          <input
            type="text"
            maxLength={18}
            value={formData.creditCode}
            onChange={(e) => {
              const val = e.target.value;
              onChange({ creditCode: val });
              if (errors.creditCode) validateField('creditCode', val);
            }}
            onBlur={(e) => validateField('creditCode', e.target.value)}
            placeholder="请输入 18 位统一社会信用代码"
            className={`w-full px-4 py-3 rounded-lg border bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:ring-2 transition-all ${errors.creditCode
              ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
              : 'border-gray-200 focus:border-primary-400/60 focus:ring-primary-100/50'
              }`}
            required
          />
          {errors.creditCode && (
            <p className="text-xs text-red-500 mt-1">{errors.creditCode}</p>
          )}
        </div>

        {/* 法定代表人信息（姓名、证件类型、证件号码） */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              <span className="text-red-500 mr-1">*</span>法定代表人姓名
            </label>
            <input
              type="text"
              value={formData.legalPerson}
              onChange={(e) => {
                const val = e.target.value;
                onChange({ legalPerson: val });
                if (errors.legalPerson) validateField('legalPerson', val);
              }}
              onBlur={(e) => validateField('legalPerson', e.target.value)}
              placeholder="请输入法定代表人姓名"
              className={`w-full px-4 py-3 rounded-lg border bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:ring-2 transition-all ${errors.legalPerson
                ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                : 'border-gray-200 focus:border-primary-400/60 focus:ring-primary-100/50'
                }`}
              required
            />
            {errors.legalPerson && (
              <p className="text-xs text-red-500 mt-1">{errors.legalPerson}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              <span className="text-red-500 mr-1">*</span>证件类型
            </label>
            <Select
              options={LEGAL_ID_TYPE_OPTIONS}
              value={currentIdType}
              onChange={(val) => onChange({ legalRepresentativeIdType: val as Api.EnterpriseOnboarding.LegalRepresentativeIdType })}
              placeholder="请选择证件类型"
              size="md"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              <span className="text-red-500 mr-1">*</span>证件号码
            </label>
            <input
              type="text"
              maxLength={20}
              value={formData.legalId}
              onChange={(e) => {
                const val = e.target.value;
                onChange({ legalId: val });
                if (errors.legalId) validateField('legalId', val);
              }}
              onBlur={(e) => validateField('legalId', e.target.value)}
              placeholder={currentIdType === 'ID_CARD' ? '请输入 18 位身份证号' : '请输入证件号码'}
              className={`w-full px-4 py-3 rounded-lg border bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:ring-2 transition-all ${errors.legalId
                ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                : 'border-gray-200 focus:border-primary-400/60 focus:ring-primary-100/50'
                }`}
              required
            />
            {errors.legalId && (
              <p className="text-xs text-red-500 mt-1">{errors.legalId}</p>
            )}
          </div>
        </div>

        {/* 运营/经办人信息（姓名、手机号、手机验证码） */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              <span className="text-red-500 mr-1">*</span>运营经办人姓名
            </label>
            <input
              type="text"
              value={formData.contactName}
              onChange={(e) => {
                const val = e.target.value;
                onChange({ contactName: val });
                if (errors.contactName) validateField('contactName', val);
              }}
              onBlur={(e) => validateField('contactName', e.target.value)}
              placeholder="请输入经办人姓名"
              className={`w-full px-4 py-3 rounded-lg border bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:ring-2 transition-all ${errors.contactName
                ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                : 'border-gray-200 focus:border-primary-400/60 focus:ring-primary-100/50'
                }`}
              required
            />
            {errors.contactName && (
              <p className="text-xs text-red-500 mt-1">{errors.contactName}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              <span className="text-red-500 mr-1">*</span>运营人员手机号
            </label>
            <input
              type="tel"
              maxLength={11}
              value={formData.contactPhone}
              onChange={(e) => {
                const val = e.target.value;
                onChange({
                  contactPhone: val,
                  operatorPhoneVerificationToken: val !== formData.contactPhone ? undefined : formData.operatorPhoneVerificationToken,
                });
                if (errors.contactPhone) validateField('contactPhone', val);
              }}
              onBlur={(e) => validateField('contactPhone', e.target.value)}
              placeholder="请输入 11 位手机号"
              className={`w-full px-4 py-3 rounded-lg border bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:ring-2 transition-all ${errors.contactPhone
                ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                : 'border-gray-200 focus:border-primary-400/60 focus:ring-primary-100/50'
                }`}
              required
            />
            {errors.contactPhone && (
              <p className="text-xs text-red-500 mt-1">{errors.contactPhone}</p>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-medium text-foreground-700">
                <span className="text-red-500 mr-1">*</span>手机验证码
              </label>
              {formData.operatorPhoneVerificationToken && (
                <span className="text-[11px] text-emerald-600 font-medium flex items-center gap-0.5">
                  <i className="ri-checkbox-circle-fill"></i> 已通过验证
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                maxLength={6}
                value={formData.operatorPhoneVerificationCode || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  onChange({
                    operatorPhoneVerificationCode: val,
                    operatorPhoneVerificationToken: undefined,
                  });
                  if (errors.verificationCode) validateField('verificationCode', val);
                }}
                placeholder="6 位验证码"
                className={`w-full px-3 py-3 rounded-lg border bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:ring-2 transition-all ${errors.verificationCode
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                  : 'border-gray-200 focus:border-primary-400/60 focus:ring-primary-100/50'
                  }`}
                required
              />
              <SendCodeButton
                onSend={handleSendCode}
                disabled={!formData.contactPhone || !PHONE_REGEX.test(formData.contactPhone)}
              />
              <button
                type="button"
                onClick={handleVerifyCode}
                disabled={verifyingCode || !(formData.operatorPhoneVerificationCode?.trim())}
                className="px-3 py-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {verifyingCode ? '校验中...' : '校验'}
              </button>
            </div>
            {errors.verificationCode && (
              <p className="text-xs text-red-500 mt-1">{errors.verificationCode}</p>
            )}
          </div>
        </div>

        {/* 核心资质证明（营业执照与授权委托书）单独上传 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ImageUploadBox
            label="营业执照照片/扫描件"
            accept="image/*,.pdf"
            value={formData.businessLicenseAsset?.access_url || formData.businessLicenseAssetId}
            onChange={(assetId) => {
              onChange({
                businessLicenseAssetId: assetId,
                businessLicenseAsset: assetId ? formData.businessLicenseAsset : undefined,
              });
            }}
            placeholder="点击或拖拽上传营业执照"
            subPlaceholder="支持 JPG、PNG、PDF 格式，大小不超过 10MB"
          />

          <ImageUploadBox
            label="法定代表人授权委托书"
            accept="image/*,.pdf"
            value={formData.authorizationAsset?.access_url || formData.authorizationAssetId}
            onChange={(assetId) => {
              onChange({
                authorizationAssetId: assetId,
                authorizationAsset: assetId ? formData.authorizationAsset : undefined,
              });
            }}
            placeholder="点击或拖拽上传授权委托书"
            subPlaceholder="支持 JPG、PNG、PDF 格式，大小不超过 10MB"
          />
        </div>

        {/* 企业图片 */}
        <div className="p-4 rounded-xl bg-gray-50 border border-gray-200/60 space-y-3">
          <div className="flex items-center justify-between">
            <label className="block text-xs font-semibold text-foreground-800">
              企业图片
            </label>
            <span className="text-xs text-foreground-500 font-mono">
              已上传 {(formData.enterpriseImages || []).length} 个材料
            </span>
          </div>

          {/* 已上传的材料多项列表 */}
          {(formData.enterpriseImages || []).length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              {(formData.enterpriseImages || []).map((imgItem, idx) => (
                <div
                  key={imgItem.upload_asset_id || idx}
                  className="p-3 rounded-lg bg-white border border-gray-200 shadow-2xs flex items-center justify-between gap-3 group"
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className="w-8 h-8 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                      <i className="ri-file-text-line text-base"></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <input
                        type="text"
                        value={imgItem.display_name || `证明材料 ${idx + 1}`}
                        onChange={(e) => {
                          const newName = e.target.value;
                          const updated = [...(formData.enterpriseImages || [])];
                          updated[idx] = { ...updated[idx], display_name: newName };
                          onChange({ enterpriseImages: updated });
                        }}
                        placeholder="材料名称"
                        className="text-xs font-medium text-foreground-900 border-b border-transparent hover:border-gray-200 focus:border-emerald-500 bg-transparent outline-none w-full truncate"
                      />
                      <div className="text-[10px] text-foreground-400 font-mono truncate">
                        ID: {imgItem.upload_asset_id}
                      </div>
                    </div>
                  </div>

                  {/* 删除材料按钮 */}
                  <button
                    type="button"
                    onClick={() => {
                      const updated = (formData.enterpriseImages || []).filter((_, i) => i !== idx);
                      const reindexed = updated.map((item, i) => ({ ...item, sort_no: i + 1 }));
                      onChange({ enterpriseImages: reindexed });
                    }}
                    className="w-7 h-7 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors shrink-0 cursor-pointer"
                    title="删除此材料"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* 上传新材料 */}
          <ImageUploadBox
            key={uploadBoxKey}
            scene="PROFILE"
            policy="public-when-published"
            placeholder="点击或拖拽添加企业图片"
            subPlaceholder="支持 JPG、PNG、PDF 格式，可上传多个企业图片"
            onChange={(assetId, file) => {
              if (assetId) {
                const currentList = formData.enterpriseImages || [];
                const newItem: Api.EnterpriseOnboarding.EnterpriseImageRequest = {
                  upload_asset_id: assetId,
                  display_name: file?.name || `证明材料 ${currentList.length + 1}`,
                  sort_no: currentList.length + 1,
                };
                onChange({ enterpriseImages: [...currentList, newItem] });
                setUploadBoxKey((k) => k + 1); // 成功添加后重置上传框，支持连续添加
                setToast({ message: `已成功添加材料 ${newItem.display_name}`, type: 'success' });
              }
            }}
          />
        </div>

        <div className="p-4 rounded-xl bg-gray-50 border border-gray-200/60">
          <h4 className="text-xs font-semibold text-foreground-700 mb-2">认证须知</h4>
          <ul className="space-y-1">
            <li className="text-xs text-foreground-500 flex items-start gap-1.5">
              <i className="ri-information-line text-secondary-500 mt-0.5"></i>
              企业名称需与营业执照完全一致
            </li>
            <li className="text-xs text-foreground-500 flex items-start gap-1.5">
              <i className="ri-information-line text-secondary-500 mt-0.5"></i>
              请核对统一社会信用代码及法定代表人身份证号
            </li>
            <li className="text-xs text-foreground-500 flex items-start gap-1.5">
              <i className="ri-information-line text-secondary-500 mt-0.5"></i>
              审核通常需要 1-3 个工作日
            </li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 py-3 rounded-lg bg-primary-500 text-foreground-950 text-sm font-semibold hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {submitting ? '提交中...' : '提交企业认证'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-3 rounded-lg border border-gray-200 text-foreground-600 text-sm hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
        </div>
      </form>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};
